/**
 * One supervisor's team: the body of "My team's metrics", and of the sidebar the
 * Supervisors page opens on a card.
 *
 * Shared so the two can never disagree, as <PersonDetail> is shared by the Employee
 * view and the drill-through. Asked for in the 2026-09-25 review: the Supervisors page
 * had its own pop-up for a team, and "there's no reason we have this special one for a
 * supervisor page. Just mirror exactly my team's metrics."
 *
 * The team is the supervisor's whole tree or their direct reports only, chosen at the
 * top and applied to every figure (2026-09-23). People with no claims are in it; the
 * supervisor is too, unless management is excluded — the default on the people tabs,
 * so a supervisor covering a few claims does not skew their own team.
 *
 * The people are one list, "My people" (components/MyPeople.tsx), which follows the
 * same switch: the hierarchy, expandable down through each supervisor under you, or
 * the direct reports. It replaced the "Your people" tree and the flat "Your whole
 * team, one list" (Shayna, 2026-10-02 review #11). The comparison is one table too,
 * "My team against comparable teams" — the Ledger-style "Team standing" was the same
 * measures again, so its bar and average became columns of it (#10; the median column
 * went in #60).
 *
 * Kept from that pop-up when it went (Yehuda, 2026-09-30): both rates in the header
 * and the team's Claims and Touches.
 *
 * State is the caller's, so each place keeps it in its own history entry for Back.
 */
import { useMemo } from 'react'
import type { ReactNode } from 'react'
import { useAppData } from '@/data/AppData'
import { useFilters } from '@/data/filters'
import type { Person } from '@/data/selectors'
import { useOpenTouchDetail } from '@/data/touchDrill'
import { useOpenClaimDetail } from '@/data/claimDrill'
import { DataTable, type Column } from '@/components/Table'
import { ExportBar } from '@/components/ExportBar'
import { MyPeople, type TeamDepth } from '@/components/MyPeople'
import { Info, Select, SupervisorTitle } from '@/components/Bits'
import { scoreGaps, formatPoints } from '@/measures/scorecard'
import {
  buildTeams,
  teamRanking,
  teamAverage,
  availableSupervisorViewMetrics,
} from '@/measures/teams'
import { rankShort } from '@/measures/rankText'
import { moneyK, money2, int, monthSpan } from '@/format'
import { defineTeam } from '@/measures/definitions'

export type { TeamDepth }

export function TeamDetail({
  supervisor: picked,
  depth,
  onDepth,
  openList,
  onOpenList,
  onOpenPerson,
  picker,
  pool,
  rankPool,
  counts,
}: {
  /** Whose team. */
  supervisor: string
  /** What "the team" means for every figure: the whole tree, or direct reports. */
  depth: TeamDepth
  onDepth: (d: TeamDepth) => void
  /** Supervisors expanded in "My people". */
  openList: string[]
  onOpenList: (next: string[]) => void
  /** A person row was clicked. */
  onOpenPerson: (name: string) => void
  /** Sits left of the "Team" control: My team's metrics' "Whose team". */
  picker?: ReactNode
  /**
   * Who the teams are built from and ranked on: `Scope.teamPool` / `Scope.rankPool`
   * unless the page says otherwise. Supervisors passes its own pools so the sidebar
   * shows the same whole team as the card that opened it.
   */
  pool?: Person[]
  rankPool?: Person[]
  /**
   * Whose labor and claims the figures count. Pass it with a pool the Management
   * toggle has not narrowed (`Scope.supervisorPool` and `Scope.teamCounts`). See
   * `buildTeams`.
   */
  counts?: (p: Person) => boolean
}) {
  const { derived, scope, coverage, label } = useAppData()
  const teamPool = pool ?? scope.teamPool
  const rankPeople = rankPool ?? scope.rankPool
  /** The months every rank in the sidebar covers, for its hover. */
  const period = monthSpan(derived.months)
  const { filters } = useFilters()

  /*
   * The team rollup and its cohort, from the same module the Supervisors cards use.
   * Built before the Supervisor filter, so choosing a supervisor there does not
   * shrink who they are ranked against.
   */
  // Whole trees always exist: a sub-team row rolls up everyone under that person
  // whichever way the depth is switched. The team's own figures follow `depth`.
  const treeTeams = useMemo(
    () => buildTeams(teamPool, derived.byName, 'tree', counts),
    [teamPool, derived.byName, counts],
  )
  const allTeams = useMemo(
    () =>
      depth === 'tree' ? treeTeams : buildTeams(teamPool, derived.byName, 'direct', counts),
    [depth, treeTeams, teamPool, derived.byName, counts],
  )
  const treeByName = useMemo(
    () => new Map(treeTeams.map((t) => [t.supervisor, t])),
    [treeTeams],
  )
  const tree = treeByName.get(picked) ?? null
  const team = useMemo(
    () => allTeams.find((t) => t.supervisor === picked) ?? null,
    [allTeams, picked],
  )
  /**
   * Ranked on the same teams built from people active in the period, whatever the Employee status
   * control shows; see `teamRanking`. The figures above follow the control.
   */
  const ranking = useMemo(
    () => teamRanking(rankPeople, derived.byName, depth, counts),
    [rankPeople, derived.byName, depth, counts],
  )
  const cohort = useMemo(() => (team ? ranking.cohort(team) : []), [ranking, team])
  /** Everyone the figures cover, less the supervisor. */
  const reports = useMemo(
    () => (team ? team.members.filter((p) => p.employee !== picked) : []),
    [team, picked],
  )
  /** Everyone below them, counted or not; the team's headcount. */
  const below = useMemo(
    () => (team ? team.everyone.filter((p) => p.employee !== picked) : []),
    [team, picked],
  )
  /** Below them but left out of the figures: management, with the toggle off. */
  const notCounted =
    (depth === 'tree' ? below.length : (team?.direct.length ?? 0)) - reports.length

  /**
   * One row per measure: the team, the cohort's average, and the rank. The
   * Ledger-style standing bar is folded in here, not a second
   * table of the same measures (Shayna, 2026-10-02 review #10).
   */
  const teamRows = useMemo(
    () =>
      team
        ? availableSupervisorViewMetrics(coverage).map((metric) => {
            return {
              metric,
              value: metric.get(team),
              // The plain mean of the comparable teams' figures; no-data teams are left
              // out. The only central figure since Shayna's review #60 (2026-10-05): "we
              // don't need average and median. choose one. average to me makes sense."
              average: teamAverage(cohort, metric),
              standing: ranking.rank(team, metric),
            }
          })
        : [],
    [team, cohort, ranking, coverage],
  )
  const openTouches = useOpenTouchDetail()
  const openClaims = useOpenClaimDetail()
  const rowsLabel = `${depth === 'tree' ? 'team' : 'direct reports'} of ${picked}`

  const overall = useMemo(
    () => (team ? ranking.overall(team, availableSupervisorViewMetrics(coverage)) : null),
    [team, ranking, coverage],
  )
  const rankBand = (r: { rank: number; of: number }) => {
    const third = Math.ceil(r.of / 3)
    return r.rank <= third ? 'good' : r.rank > r.of - third ? 'bad' : 'mid'
  }
  /** Who a rank here is against, said on the rank itself (Shayna, 2026-10-02). */
  const explain = (metric: Parameters<typeof ranking.explain>[1]) =>
    team ? ranking.explain(team, metric, availableSupervisorViewMetrics(coverage), period) : ''

  type TeamRow = (typeof teamRows)[number]

  const teamColumns: Column<TeamRow>[] = [
    {
      key: 'measure',
      header: 'Measure',
      width: 240,
      render: (r) => (
        <span style={{ fontSize: 'var(--t-table)', fontWeight: 500 }}>
          {label(r.metric.label)}
          <Info def={defineTeam(r.metric.key)} />
        </span>
      ),
    },
    {
      key: 'mine',
      header: 'My team',
      align: 'right',
      width: 130,
      render: (r) => (
        <span
          className="mono"
          style={{
            fontSize: 'var(--t-table)',
            // Terciles, the same bands the rest of the app uses. No band without a
            // cohort — a colour implies a comparison that was not made.
            color:
              r.standing === null
                ? 'var(--ink)'
                : r.standing.pctile <= 1 / 3
                  ? 'var(--good-ink)'
                  : r.standing.pctile >= 2 / 3
                    ? 'var(--crit-ink)'
                    : 'var(--ink)',
            // Bold only when coloured, as the Ledger: the band is the point.
            fontWeight:
              r.standing !== null && (r.standing.pctile <= 1 / 3 || r.standing.pctile >= 2 / 3)
                ? 600
                : 400,
          }}
        >
          {r.metric.fmt(r.value)}
        </span>
      ),
    },
    {
      // The Ledger's standing bar: longer is better, whichever way the measure runs.
      key: 'bar',
      header: 'Standing',
      width: 140,
      render: (r) =>
        r.standing ? (
          <span className="standbar" title={explain(r.metric)}>
            <i
              style={{
                width: `${Math.max(3, (1 - r.standing.pctile) * 100)}%`,
                background:
                  1 - r.standing.pctile >= 2 / 3
                    ? 'var(--good)'
                    : 1 - r.standing.pctile >= 1 / 3
                      ? 'var(--s1)'
                      : 'var(--crit)',
              }}
            />
          </span>
        ) : null,
    },
    {
      key: 'avg',
      header: 'Comparable average',
      align: 'right',
      width: 160,
      dim: true,
      render: (r) => (
        <span className="mono" style={{ fontSize: 'var(--t-table)' }}>
          {r.metric.fmt(r.average)}
        </span>
      ),
    },
    {
      key: 'rank',
      header: 'My rank',
      align: 'right',
      width: 110,
      render: (r) => (
        <span className="mono" style={{ fontSize: 'var(--t-table)' }} title={explain(r.metric)}>
          {r.standing ? (
            <span
              title={
                r.metric.fmt(r.value) === r.metric.fmt(r.standing.value)
                  ? undefined
                  : `${explain(r.metric)} — ranked on the months people were active: ${r.metric.fmt(r.standing.value)}`
              }
            >
              {r.standing.rank}
              <span style={{ color: 'var(--ink-3)' }}>/{r.standing.of}</span>
            </span>
          ) : team && ranking.unranked(team) ? (
            <span style={{ color: 'var(--ink-3)' }}>—</span>
          ) : (
            <span style={{ color: 'var(--ink-3)' }}>
              {r.value === null ? 'no data' : 'no cohort'}
            </span>
          )}
        </span>
      ),
    },
  ]

  /**
   * Each report's dearest unearned point, with how many other lines are still open.
   * Lowest score first, as the Ledger orders them, so the reports who most need the
   * supervisor come first. Reports with no score go last.
   */
  const actions = useMemo(
    () =>
      [...reports]
        .sort(
          (a, b) =>
            (a.score.scored ? a.score.total : 99) - (b.score.scored ? b.score.total : 99),
        )
        .map((person) => ({ person, gaps: scoreGaps(person.score) }))
        .filter((a) => a.gaps.length > 0),
    [reports],
  )

  return (
    <>
      {/*
        Whose team, and what counts as their team, side by side: the second choice
        changes every figure below AND who "My people" lists, so it sits with the
        first rather than down on the people table (Shayna, 2026-09-23). One control
        for both, never a second one on the list.
      */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'flex-end', paddingBottom: 14 }}>
        {picker}
        {tree && (
          <div style={{ width: 340, maxWidth: '100%' }}>
            <Select
              label="Team"
              value={depth}
              options={[
                {
                  value: 'tree',
                  label: `My whole team — every level below (${int(tree.everyone.filter((p) => p.employee !== picked).length)})`,
                },
                { value: 'direct', label: `Just my direct reports (${int(tree.direct.length)})` },
              ]}
              onChange={(v) => onDepth(v as TeamDepth)}
            />
          </div>
        )}
      </div>

      {!team ? (
        <div className="empty-note">No supervisor matches the current filters.</div>
      ) : (
        <>
          <div className="mehero" style={{ marginTop: 0 }}>
            <div className="mehero-who">
              <h3>{picked}</h3>
              <div className="mehero-meta">
                <SupervisorTitle t={team} />
              </div>
              {/*
                Spelled out, because "direct reports 4 · whole team 5" did not explain
                itself in the review: the whole team is everyone below at every level.
              */}
              <div className="mehero-meta">
                {depth === 'tree'
                  ? `Your whole team: ${int(below.length)} people at every level below you, ${int(team.direct.length)} of them reporting to you directly`
                  : `Your direct reports: ${int(team.direct.length)} people`}
                {/* Headcounts are everyone; the figures after them are the people counted. */}
                {notCounted > 0 && ` · ${int(notCounted)} of them management, left out of the figures`}{' '}
                · {int(team.carrying.length)} carrying claims · {int(team.billed)} billed
                claims · {moneyK(team.labor)} payroll labor
              </div>
              {/*
                Both rates, whichever way the switch is set, as the Supervisors page's
                pop-up showed them before it became this sidebar (kept by Yehuda,
                2026-09-30). The rest of the figures follow the switch.
              */}
              <div className="mehero-meta">
                Whole team <b>{money2(tree?.costPerClaim ?? null)}</b> / claim · direct reports
                only <b>{money2(tree?.directCostPerClaim ?? null)}</b> / claim
                {team.self
                  ? ' · includes your own labor and claims'
                  : !team.selfInData
                    ? ' · your own salary is not in this data'
                    : filters.management !== 'all'
                      ? ' · management excluded, so your own labor and claims are left out'
                      : ''}
              </div>
              <div className="mehero-meta" data-testid="rank-line">
                {explain(null)}
                {!ranking.unranked(team) && ' — supervisors with your job title, people active in the period'}
              </div>
              {/* Everyone the figures cover, so the switch decides whose rows these are. */}
              <div className="mehero-meta" style={{ marginTop: 7 }}>
                <span className="sub2">The team’s underlying rows</span>
                <button
                  type="button"
                  className="byemp"
                  onClick={() =>
                    openClaims({ employees: team.members.map((p) => p.employee), label: rowsLabel })
                  }
                >
                  Claims
                </button>
                <button
                  type="button"
                  className="byemp"
                  onClick={() =>
                    openTouches({
                      employees: team.members.map((p) => p.employee),
                      label: rowsLabel,
                      basis: 'claim',
                    })
                  }
                >
                  Touches
                </button>
              </div>
            </div>
            {overall && overall.rank !== null && (
              <div className={`mescore ${rankBand({ rank: overall.rank, of: overall.of })}`} title={explain(null)}>
                <em>your rank</em>
                <b>{overall.rank}</b>
                <span>of {overall.of}</span>
              </div>
            )}
            {overall && overall.rank === null && rankShort(overall) === 'no data' && (
              <div className="mescore" title={explain(null)}>
                <em>your rank</em>
                <b>—</b>
                <span>no data</span>
              </div>
            )}
          </div>

          {/*
            "My team against comparable teams" — measure, this team, the standing bar,
            the cohort average, and the rank (no median since review #60). One table since Shayna's
            2026-10-02 review (#10): "Team standing" showed the same measures again. A rate like $6.36 a claim means nothing until it
            is set against teams doing the same work.

            Cohort and rollup come from measures/teams.ts, the same code the
            Supervisors cards use. They were separate implementations until today,
            which is how two pages end up quoting different team cost per claim.
          */}
          <section className="section">
            <h2 className="section-heading">My team against comparable teams</h2>
            <ExportBar
              filename="team-against-comparable-teams.csv"
              rows={teamRows}
              columns={[
                { header: 'Measure', value: (r) => label(r.metric.label) },
                { header: 'My team', value: (r) => r.value },
                { header: 'Comparable average', value: (r) => r.average },
                { header: 'My rank', value: (r) => r.standing?.rank ?? null },
                { header: 'Of', value: (r) => r.standing?.of ?? null },
              ]}
            />
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <DataTable
                storageKey="team-detail.measures"
                // No sort: one row per measure, in the measures’ own order.
                rows={teamRows}
                columns={teamColumns}
                rowKey={(r) => r.metric.key}
                empty="No comparable measures in this bundle."
              />
            </div>
          </section>

          <MyPeople
            supervisor={picked}
            depth={depth}
            openList={openList}
            onOpenList={onOpenList}
            onOpenPerson={onOpenPerson}
            pool={pool}
            counts={counts}
          />

          <section className="section">
            <h2 className="section-heading">Where to spend your time</h2>
            <p className="body-note" style={{ margin: '6px 0 18px' }}>
              The single cheapest point available to each report who is not already at
              the top.
            </p>
            {actions.length === 0 ? (
              <div className="co good">
                <h4>Nothing outstanding</h4>
                <p>Every report is earning every point the scorecard offers.</p>
              </div>
            ) : (
              actions.map(({ person, gaps }) => {
                const best = gaps[0]!
                const others = gaps.length - 1
                return (
                  <div key={person.employee} className="co warn" style={{ marginBottom: 10 }}>
                    <h4>
                      {person.employee}{' '}
                      <span style={{ color: 'var(--ink-3)', fontSize: 11, fontWeight: 400 }}>
                        · {person.jobTitle}
                        {person.score.scored && ` · scoring ${formatPoints(person.score.total)}`}
                      </span>
                    </h4>
                    <span className="co-big">
                      {best.need}{' '}
                      <span className="pts-chip good">{formatPoints(best.pts)}</span>
                    </span>
                    <p>
                      {best.detail}
                      {others > 1
                        ? ` There are ${others} other points available beyond this one.`
                        : others === 1
                          ? ' There is one other point available beyond this one.'
                          : ''}
                    </p>
                  </div>
                )
              })
            )}
          </section>
        </>
      )}
    </>
  )
}
